import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchIcon, FilterIcon, MapPinIcon, CheckCircleIcon, GridIcon, ListIcon, MapIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { developers } from '../utils/data';
import { Developer } from '../utils/types';
import Pagination from '../components/ui/Pagination';
import { TalentCapsuleCard } from '../components/developers/TalentCapsuleCard';
type ViewMode = 'grid' | 'spotlight' | 'collections' | 'map';
const Developers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    position: '',
    country: '',
    skills: [] as string[]
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const developersPerPage = 8;
  // Certification exams data with ALL required properties
  const certificationExams = [{
    id: '1',
    name: 'Laravel Advanced Certification',
    description: 'Master advanced Laravel concepts including queues, events, broadcasting, and advanced Eloquent techniques.',
    price: 149,
    currency: 'USD',
    duration: '180',
    questions: 50,
    technologies: ['Laravel', 'PHP', 'MySQL'],
    topics: ['Advanced Eloquent', 'Queue Management', 'Event Broadcasting', 'API Development', 'Testing'],
    level: 'Advanced',
    badge: 'https://via.placeholder.com/80?text=Laravel'
  }, {
    id: '2',
    name: 'Vue.js Professional Certification',
    description: 'Comprehensive Vue.js certification covering Composition API, Vuex, Vue Router, and best practices.',
    price: 129,
    currency: 'USD',
    duration: '150',
    questions: 45,
    technologies: ['Vue.js', 'JavaScript', 'Vuex'],
    topics: ['Composition API', 'State Management', 'Vue Router', 'Component Design', 'Performance'],
    level: 'Professional',
    badge: 'https://via.placeholder.com/80?text=Vue'
  }, {
    id: '3',
    name: 'Full-Stack Laravel Developer',
    description: 'Complete full-stack certification combining Laravel backend with modern frontend frameworks.',
    price: 199,
    currency: 'USD',
    duration: '240',
    questions: 60,
    technologies: ['Laravel', 'Vue.js', 'React', 'API Development'],
    topics: ['Backend Architecture', 'Frontend Integration', 'API Design', 'Authentication', 'Deployment'],
    level: 'Expert',
    badge: 'https://via.placeholder.com/80?text=Full-Stack'
  }, {
    id: '4',
    name: 'React & Laravel Integration',
    description: 'Learn to build modern SPAs with React frontend and Laravel API backend.',
    price: 139,
    currency: 'USD',
    duration: '180',
    questions: 40,
    technologies: ['React', 'Laravel', 'REST APIs'],
    topics: ['React Fundamentals', 'Laravel API', 'State Management', 'Authentication'],
    level: 'Intermediate',
    badge: 'https://via.placeholder.com/80?text=React'
  }];
  // Quick filter chips
  const quickFilters = [{
    label: 'Laravel',
    type: 'skill',
    value: 'Laravel'
  }, {
    label: 'Vue.js',
    type: 'skill',
    value: 'Vue.js'
  }, {
    label: 'React',
    type: 'skill',
    value: 'React'
  }, {
    label: 'Full-stack',
    type: 'role',
    value: 'Full-stack Developer'
  }, {
    label: 'Available now',
    type: 'status',
    value: 'available'
  }, {
    label: '<€75/hr',
    type: 'rate',
    value: '75'
  }];
  // Get unique values for filters
  const countries = [...new Set(developers.map(dev => dev.country))];
  const roles = [...new Set(developers.map(dev => dev.role))];
  const positions = [...new Set(developers.map(dev => dev.position))];
  const statuses = ['available', 'unavailable', 'looking'];
  const allSkills = [...new Set(developers.flatMap(dev => dev.skills.map(s => s.name)))];
  // Filter developers
  const filteredDevelopers = developers.filter(dev => {
    const matchesSearch = searchTerm === '' || dev.name.toLowerCase().includes(searchTerm.toLowerCase()) || dev.title.toLowerCase().includes(searchTerm.toLowerCase()) || dev.skills.some(skill => skill.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filters.status === '' || dev.status === filters.status;
    const matchesRole = filters.role === '' || dev.role === filters.role;
    const matchesPosition = filters.position === '' || dev.position === filters.position;
    const matchesCountry = filters.country === '' || dev.country === filters.country;
    const matchesSkills = filters.skills.length === 0 || filters.skills.every(skillName => dev.skills.some(s => s.name === skillName));
    return matchesSearch && matchesStatus && matchesRole && matchesPosition && matchesCountry && matchesSkills;
  });
  // Pagination
  const indexOfLastDeveloper = currentPage * developersPerPage;
  const indexOfFirstDeveloper = indexOfLastDeveloper - developersPerPage;
  const currentDevelopers = filteredDevelopers.slice(indexOfFirstDeveloper, indexOfLastDeveloper);
  const totalPages = Math.ceil(filteredDevelopers.length / developersPerPage);
  const handleQuickFilter = (type: string, value: string) => {
    if (type === 'skill') {
      setFilters(prev => {
        if (prev.skills.includes(value)) {
          return {
            ...prev,
            skills: prev.skills.filter(s => s !== value)
          };
        } else {
          return {
            ...prev,
            skills: [...prev.skills, value]
          };
        }
      });
    } else if (type === 'status') {
      setFilters(prev => ({
        ...prev,
        status: prev.status === value ? '' : value
      }));
    } else if (type === 'role') {
      setFilters(prev => ({
        ...prev,
        role: prev.role === value ? '' : value
      }));
    }
  };
  const clearFilters = () => {
    setFilters({
      status: '',
      role: '',
      position: '',
      country: '',
      skills: []
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  const isFilterActive = (type: string, value: string) => {
    if (type === 'skill') return filters.skills.includes(value);
    if (type === 'status') return filters.status === value;
    if (type === 'role') return filters.role === value;
    return false;
  };
  return <div className="bg-[#0F1115] min-h-screen text-gray-300" data-id="element-639">
      {/* Editorial Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F1115] via-[#1a1d24] to-[#111827] text-white overflow-hidden" data-id="element-640">
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.015] bg-grain" data-id="element-641"></div>

        {/* PHP Code Background Image */}
        <div className="absolute inset-0 opacity-[0.15] overflow-hidden" data-id="element-642">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/koTEbxE6H3NVaj4P9TF4mh/Schermafbeelding_2025-10-11_150904.png" alt="PHP Code Background" className="w-full h-full object-cover object-center" data-id="element-643" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10" data-id="element-644">
          <div className="max-w-4xl mx-auto text-center" data-id="element-645">
            <h1 className="font-display text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight text-white" data-id="element-646">
              Verified developers you can hire with confidence.
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-647">
              Search certified back- & front-end talent. Real badges, live
              availability, transparent rates.
            </p>

            {/* Primary & Secondary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-648">
              <a href="#browse" className="bg-[#d73b3e] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-649">
                Browse certified talent
              </a>
              <Link to="/verification" className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-200 border border-white/20" data-id="element-650">
                How verification works
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400" data-id="element-651">
              <div className="flex items-center gap-2" data-id="element-652">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-653" />
                <span data-id="element-654">3,500+ developers certified since 2018</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-655">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-656" />
                <span data-id="element-657">Avg. 7 days to shortlist</span>
              </div>
            </div>
          </div>
        </div>

        {/* Curved divider */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-658">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-659">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0F1115" data-id="element-660" />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" id="browse" data-id="element-661">
        {/* Discovery Modes & Quick Filters */}
        <div className="mb-8" data-id="element-662">
          {/* View Mode Toggles */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-663">
            <div className="flex gap-2 bg-[#1a1d24] rounded-full p-1 shadow-sm border border-gray-800" data-id="element-664">
              <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-665">
                <GridIcon className="h-4 w-4 inline mr-1" data-id="element-666" />
                Grid
              </button>
              <button onClick={() => setViewMode('spotlight')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'spotlight' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-667">
                <StarIcon className="h-4 w-4 inline mr-1" data-id="element-668" />
                Spotlight
              </button>
              <button onClick={() => setViewMode('collections')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'collections' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-669">
                <ListIcon className="h-4 w-4 inline mr-1" data-id="element-670" />
                Collections
              </button>
              <button onClick={() => setViewMode('map')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'map' ? 'bg-[#d73b3e] text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-671">
                <MapIcon className="h-4 w-4 inline mr-1" data-id="element-672" />
                Map
              </button>
            </div>

            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="lg:hidden flex items-center gap-2 px-4 py-2 bg-[#1a1d24] rounded-full border border-gray-800 text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors duration-200" data-id="element-673">
              <FilterIcon className="h-4 w-4" data-id="element-674" />
              Advanced filters
            </button>
          </div>

          {/* Quick Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-675">
            {quickFilters.map(filter => <button key={filter.label} onClick={() => handleQuickFilter(filter.type, filter.value)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isFilterActive(filter.type, filter.value) ? 'bg-[#d73b3e] text-white shadow-md' : 'bg-[#1a1d24] text-gray-300 border border-gray-800 hover:border-gray-700 hover:text-white'}`} data-id="element-676">
                {filter.label}
              </button>)}
            {(filters.skills.length > 0 || filters.status || filters.role) && <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium text-[#d73b3e] hover:bg-red-500/10 transition-colors duration-200" data-id="element-677">
                Clear all
              </button>}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-678">
          {/* Advanced Filters Sidebar */}
          <div className={`lg:w-1/4 ${isFilterOpen ? 'block' : 'hidden lg:block'}`} data-id="element-679">
            <div className="bg-[#1a1d24] rounded-2xl p-6 sticky top-24 shadow-sm border border-gray-800" data-id="element-680">
              <div className="flex justify-between items-center mb-6" data-id="element-681">
                <h2 className="text-lg font-semibold text-white" data-id="element-682">
                  Narrow results
                </h2>
              </div>

              {/* Search */}
              <div className="mb-6" data-id="element-683">
                <label htmlFor="search" className="block text-sm font-medium text-gray-400 mb-2" data-id="element-684">
                  Search
                </label>
                <div className="relative" data-id="element-685">
                  <input type="text" id="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-4 py-3 pr-10 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm placeholder-gray-600" placeholder="Name, title, or skill..." data-id="element-686" />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-687">
                    <SearchIcon className="h-4 w-4 text-gray-500" data-id="element-688" />
                  </div>
                </div>
              </div>

              {/* Status Filter */}
              <div className="mb-6" data-id="element-689">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-690">
                  Status
                </label>
                <select value={filters.status} onChange={e => setFilters({
                ...filters,
                status: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-691">
                  <option value="" data-id="element-692">All Statuses</option>
                  {statuses.map(status => <option key={status} value={status} data-id="element-693">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>)}
                </select>
              </div>

              {/* Role Filter */}
              <div className="mb-6" data-id="element-694">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-695">
                  Role
                </label>
                <select value={filters.role} onChange={e => setFilters({
                ...filters,
                role: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-696">
                  <option value="" data-id="element-697">All Roles</option>
                  {roles.map(role => <option key={role} value={role} data-id="element-698">
                      {role}
                    </option>)}
                </select>
              </div>

              {/* Position Filter */}
              <div className="mb-6" data-id="element-699">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-700">
                  Position
                </label>
                <select value={filters.position} onChange={e => setFilters({
                ...filters,
                position: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-701">
                  <option value="" data-id="element-702">All Positions</option>
                  {positions.map(position => <option key={position} value={position} data-id="element-703">
                      {position}
                    </option>)}
                </select>
              </div>

              {/* Country Filter */}
              <div className="mb-6" data-id="element-704">
                <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-705">
                  Country
                </label>
                <select value={filters.country} onChange={e => setFilters({
                ...filters,
                country: e.target.value
              })} className="block w-full rounded-xl border border-gray-700 bg-[#0F1115] text-white px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-706">
                  <option value="" data-id="element-707">All Countries</option>
                  {countries.map(country => <option key={country} value={country} data-id="element-708">
                      {country}
                    </option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Developers Grid */}
          <div className="lg:w-3/4" data-id="element-709">
            {/* Results count */}
            <div className="flex justify-between items-center mb-6" data-id="element-710">
              <p className="text-sm text-gray-400" data-id="element-711">
                Showing{' '}
                <span className="font-semibold text-white" data-id="element-712">
                  {filteredDevelopers.length}
                </span>{' '}
                certified developers
              </p>
            </div>

            {filteredDevelopers.length === 0 ? <div className="bg-[#1a1d24] rounded-2xl p-12 text-center border border-gray-800" data-id="element-713">
                <div className="mb-6" data-id="element-714">
                  <SearchIcon className="h-16 w-16 text-gray-600 mx-auto" data-id="element-715" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-716">
                  No exact matches
                </h3>
                <p className="text-gray-400 mb-6" data-id="element-717">
                  Try widening your rate or region filters
                </p>
                <div className="flex flex-wrap gap-2 justify-center" data-id="element-718">
                  <button onClick={() => setFilters({
                ...filters,
                country: ''
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-719">
                    EU
                  </button>
                  <button onClick={() => setFilters({
                ...filters,
                status: 'available'
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-720">
                    Remote
                  </button>
                  <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors duration-200" data-id="element-721">
                    €50–€90/hr
                  </button>
                </div>
              </div> : <>
                {viewMode === 'grid' && <div className="grid md:grid-cols-2 gap-6" data-id="element-722">
                    {currentDevelopers.map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-723" />)}
                  </div>}

                {viewMode === 'spotlight' && <div className="space-y-6" data-id="element-724">
                    {/* Featured large card */}
                    {currentDevelopers.length > 0 && <TalentCapsuleCard developer={currentDevelopers[0]} featured data-id="element-725" />}
                    {/* Two smaller cards */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-726">
                      {currentDevelopers.slice(1, 3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-727" />)}
                    </div>
                    {/* Rest in grid */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-728">
                      {currentDevelopers.slice(3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-729" />)}
                    </div>
                  </div>}

                {viewMode === 'collections' && <div className="space-y-8" data-id="element-730">
                    <div data-id="element-731">
                      <h3 className="text-2xl font-bold text-white mb-4" data-id="element-732">
                        Top Laravel Agencies
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-733">
                        {currentDevelopers.slice(0, 4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-734" />)}
                      </div>
                    </div>
                    <div data-id="element-735">
                      <h3 className="text-2xl font-bold text-white mb-4" data-id="element-736">
                        New This Week
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-737">
                        {currentDevelopers.slice(4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-738" />)}
                      </div>
                    </div>
                  </div>}

                {viewMode === 'map' && <div className="bg-[#1a1d24] rounded-2xl p-8 border border-gray-800 text-center" data-id="element-739">
                    <MapIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" data-id="element-740" />
                    <h3 className="text-xl font-semibold text-white mb-2" data-id="element-741">
                      Map view coming soon
                    </h3>
                    <p className="text-gray-400" data-id="element-742">
                      Visualize developers by location on an interactive map
                    </p>
                  </div>}
              </>}

            {/* Pagination */}
            {filteredDevelopers.length > 0 && viewMode === 'grid' && <div className="mt-8 flex justify-center" data-id="element-743">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-744" />
              </div>}
          </div>
        </div>
      </div>

      {/* Certification Exams Section */}
      <section id="certifications" className="py-20 bg-gradient-to-br from-[#1a1d24] to-[#0F1115]" data-id="element-745">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-746">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-747">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-748">
              Get Certified
            </h2>
            <p className="text-lg text-gray-400" data-id="element-749">
              Validate your expertise with industry-recognized certifications
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-750">
            {certificationExams.map(exam => <div key={exam.id} className="bg-[#1a1d24] rounded-2xl shadow-lg border border-gray-800 hover:border-gray-700 transition-all duration-300 overflow-hidden group" data-id="element-751">
                <div className="p-8" data-id="element-752">
                  {/* Badge */}
                  <div className="flex justify-center mb-6" data-id="element-753">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#d73b3e] to-[#c02f32] rounded-full flex items-center justify-center shadow-lg p-3" data-id="element-754">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg" alt="Laravel" className="w-full h-full object-contain" data-id="element-755" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center" data-id="element-756">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-757">
                      {exam.name}
                    </h3>
                    <p className="text-gray-400 mb-6 text-sm leading-relaxed" data-id="element-758">
                      {exam.description}
                    </p>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-[#0F1115] rounded-xl border border-gray-800" data-id="element-759">
                      <div data-id="element-760">
                        <div className="text-2xl font-bold text-[#d73b3e]" data-id="element-761">
                          {exam.questions || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-762">
                          Questions
                        </div>
                      </div>
                      <div data-id="element-763">
                        <div className="text-2xl font-bold text-[#d73b3e]" data-id="element-764">
                          {exam.duration || '0'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-765">
                          Minutes
                        </div>
                      </div>
                    </div>

                    {/* Topics */}
                    <div className="mb-6" data-id="element-766">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3" data-id="element-767">
                        Topics Covered:
                      </h4>
                      <div className="flex flex-wrap gap-2 justify-center" data-id="element-768">
                        {(exam.topics || []).slice(0, 3).map((topic, index) => <span key={index} className="text-xs bg-gray-800 text-gray-300 px-3 py-1.5 rounded-full font-medium border border-gray-700" data-id="element-769">
                            {topic}
                          </span>)}
                        {(exam.topics || []).length > 3 && <span className="text-xs bg-gray-800 text-gray-300 px-3 py-1.5 rounded-full font-medium border border-gray-700" data-id="element-770">
                            +{(exam.topics || []).length - 3} more
                          </span>}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <button className="w-full px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-200 shadow-sm group-hover:shadow-md" data-id="element-771">
                      Start Certification
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
};
export default Developers;